import React, { useState, useEffect } from "react";
import { api } from "../../../api";
import { useQuery } from "react-query";
import Card from "./components/Card";
import { exams as examsApi } from "../../../api/exams";
import { useTranslation } from "react-i18next";
import Loader from "../../../components/loader/Loader";
import Pagination from "../../../components/Pagination/Pagination";
import Input from "../../../components/input/Input";
import EmptyData from "../../../components/EmptyData";
import SurveyCard from "./components/SurveyCard";
import handsImage from "../../../images/twemoji_waving-hand.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAddressBook,
  faTimeline,
  faUser,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useSearchParams } from "react-router-dom";
import { branding } from "../../../api/branding";
import Modal from "../survey/components/Modal"; // added

const Dashboard = ({ width }) => {
  const [showRedirect, hideRedirect] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showEligibilityModal, setShowEligibilityModal] = useState(false);
  const user = JSON.parse(sessionStorage.getItem("user"));
  // Culture audit has no reviewer/360 concept — sub_type is only present on
  // the survey magic-link login response (see CultureAuditLogin.tsx);
  // 360/appraisal logins never set it, so this stays false for them.
  const isCultureAudit = user?.sub_type === "culture";
  const { t, i18n: i18nNext } = useTranslation();

  const { data: summaryData, isLoading: summaryLoading } = useQuery(
    ["Self survey Summary Data"],
    async () => {
      const data = await api.getDashboardSummary(user?.user?.user_id, user?.client_id);
      return data;
    },
    {
      select: (response) => response?.data,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      onError: (error) => {
        if (error?.response?.status === 400) {
          setShowEligibilityModal(true);
        }
      }
    }
  );

  const [searchParams] = useSearchParams();
  const domain = searchParams.get("domain") || sessionStorage.getItem("domain");

  const storedBranding = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("branding"));
    } catch (error) {
      return null;
    }
  })();

  const { data: brandingData } = useQuery(
    ["branding", domain],
    async () => {
      const data = await branding.getBranding({ domain });
      return data;
    },
    {
      select: (data) => data,
      refetchOnWindowFocus: false,
      enabled: !storedBranding && !!domain,
      initialData: storedBranding,
      onSuccess: (data) => {
        sessionStorage.setItem("branding", JSON.stringify(data));
      }
    }
  );

  const appearance = brandingData?.data?.appearance;

  useEffect(() => {
    hideRedirect(true);
  }, []);

  useEffect(() => {
    const shown = localStorage.getItem("welcomeShown") === "true";
    if (!shown) return setShowWelcomeModal(true);
    setShowWelcomeModal(false);
  }, []);

  const handleCloseWelcome = (visible) => {
    // Modal passes false when closing; always persist that we've shown it
    setShowWelcomeModal(visible);
    try {
      localStorage.setItem("welcomeShown", "true");
    } catch (e) {
      // ignore storage errors
    }
  };

  return (
    <React.Fragment>
      {appearance && (
        <style>
          {`
            .branding-text {
               color: ${appearance.primary_text_colour} !important;
            }
            .branding-button {
               background-color: ${appearance.primary_button_colour} !important;
               color: ${appearance.menu_text_colour} !important;
            }
            .branding-icon {
               color: ${appearance.theme_colour} !important;
            }
            .branding-font {
               font-family: ${appearance.font}, sans-serif !important;
            }
          `}
        </style>
      )}
      {showWelcomeModal && (
        <Modal closeModal={handleCloseWelcome} title={"Welcome to 360° Feedback!"}>
          <div className="space-y-4 text-md text-gray-700">
            <p className="text-xl">
              Welcome! This tool is here to support you and your team in understanding strengths and uncovering opportunities for growth.
            </p>

            <div className="space-y-2">
              <p className="text-xl">What you should know:</p>
              <ul className="list-disc text-md pl-5">
                <li style={{ fontSize: '16px' }}>You are here to give feedback for your self or for your team members.</li>
                <li style={{ fontSize: '16px' }}>Set aside a few minutes to thoughtfully answer the surveys for yourself and your team.</li>
                <li style={{ fontSize: '16px' }}>Keep your feedback clear and helpful — real examples make it even better.</li>
                <li style={{ fontSize: '16px' }}>Give a honest feedback either to your self or team mate that will help in idenfying areas of strengths and weakness</li>
                <li style={{ fontSize: '16px' }}>Feel free to Save Draft at any time and return whenever you're ready.</li>
              </ul>
            </div>
            <p className="text-xl">
              Your input plays an important role in supporting personal and team development. Thanks for taking the time to contribute!
            </p>
          </div>
        </Modal>
      )}
      {showEligibilityModal && (
        <Modal closeModal={setShowEligibilityModal} title="Eligibility notice">
          <div className="py-4">
            <p className="text-gray-700 text-lg">
              Report unavailable; some required raters did not submit feedback.
            </p>
          </div>
        </Modal>
      )}
      <div className={appearance ? 'branding-font' : ''}>
        <>
          <div className="md:flex bg-white py-5 px-3 items-center justify-between w-full">
            <div className="w-full">
              <h1
                className="text-darkBlue flex flex-wrap items-center font-semibold text-lg md:text-[22px]"
                style={{ marginTop: "0px" }}
              >
                <img src={handsImage} className="mr-2 w-6 h-6 md:w-auto md:h-auto" alt="hands" />{" "}
                <span className={`mr-2 md:text-3xl ${appearance ? 'branding-text' : ''}`}>{t("welcome")},</span>{" "}
                <span className={`text-xl md:text-2xl px-1 md:px-3 font-normal ${appearance ? 'branding-text' : 'text-gray-700'}`}>
                  {user?.user?.name || user?.name}!
                </span>
                <span className="w-full md:text-3xl sm:w-auto mt-2 sm:mt-0 sm:ml-2">
                  Take a quick survey to get started
                </span>
              </h1>
            </div>
          </div>
          {/*( showRedirect && mockAssessment?.length) ? 
               <Redirect handleRedirect={hideRedirect} /> : null 
           */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
            <div className="flex flex-col gap-4 bg-white p-5 py-16 border rounded">
              <div className="flex justify-between items-center">
                <div className="flex items-center ml-3">
                  <FontAwesomeIcon
                    className={`text-5xl ${appearance ? 'branding-icon' : 'text-orange-500'}`}
                    icon={faTimeline}
                  />
                  <div className="ml-3">
                    <h1 className="font-semibold text-lg ml-2">Self Review</h1>
                    <p>Pending completion ({summaryData?.self_reviews?.selfReviewPending || 0})</p>
                  </div>
                </div>
                <div className="ml-3">
                  <h1 className="font-semibold text-2xl ml-2">
                    {summaryData?.self_reviews?.selfReviewAssigned || 0}
                  </h1>
                  <p>assigned</p>
                </div>
              </div>
              <Link
                to="/candidate/self-review"
                className={`mt-5block w-full text-center rounded-lg text-white py-3 px-16 mt-5 ${appearance ? 'branding-button' : 'bg-orange-500'}`}
              >
                View Details
              </Link>
            </div>
            {!isCultureAudit && (
              <div className="flex flex-col gap-4 bg-white p-5 py-16 border rounded">
                <div className="flex justify-between items-center">
                  <div className="flex items-center ml-3">
                    <FontAwesomeIcon
                      className={`text-5xl ${appearance ? 'branding-icon' : 'text-primary'}`}
                      icon={faUsers}
                    />
                    <div className="ml-3">
                      <h1 className="font-semibold text-lg ml-2">To Review</h1>
                      <p>
                        Pending evaluation awaiting your though (
                        {summaryData?.evaluations?.evaluationPending || 0})
                      </p>
                    </div>
                  </div>
                  <div className="ml-3">
                    <h1 className="font-semibold text-2xl ml-2">
                      {summaryData?.evaluations?.evaluationAssigned || 0}
                    </h1>
                    <p>assigned</p>
                  </div>
                </div>
                <Link
                  to="/candidate/my-review"
                  className={`mt-5block w-full text-center rounded-lg text-white py-3 px-16 mt-5 ${appearance ? 'branding-button' : 'bg-primary'}`}
                >
                  View Details
                </Link>
              </div>
            )}
            {!isCultureAudit && (
              <div className="flex flex-col gap-4 bg-white p-5 py-16 border rounded">
                <div className="flex justify-between items-center">
                  <div className="flex items-center ml-3">
                    <FontAwesomeIcon
                      className={`text-5xl ${appearance ? 'branding-icon' : 'text-primary'}`}
                      icon={faUser}
                    />
                    <div className="ml-3">
                      <h1 className="font-semibold text-lg ml-2">My Reviewers</h1>
                      <p>
                        Pending ({summaryData?.my_reviewers?.myReviewersPending || 0})
                      </p>
                    </div>
                  </div>
                  <div className="ml-3">
                    <h1 className="font-semibold text-2xl ml-2">
                      {summaryData?.my_reviewers?.myReviewersAssigned || 0}
                    </h1>
                    <p>assigned</p>
                  </div>
                </div>
                <Link
                  to="/candidate/reviewers"
                  className={`mt-5block w-full text-center rounded-lg text-white py-3 px-16 mt-5 ${appearance ? 'branding-button' : 'bg-primary'}`}
                >
                  View Details
                </Link>
              </div>
            )}
          </div>
          {!isCultureAudit && (
            <div className="mt-10 bg-white p-5 border rounded-lg">
              <h1 className="text-2xl font-semibold">Quick Action</h1>
              <div className="flex">
                <Link to="/candidate/report"
                  className={`border mt-5  text-center rounded-lg py-3 px-5 ${appearance ? '' : ''}`}
                >
                  <span className="flex items-center">
                    <FontAwesomeIcon className={`text-5xl ${appearance ? 'branding-icon' : 'text-gray-500'}`} icon={faAddressBook} />
                    <span className="ml-3 justify-start flex flex-col">
                      <span className="text-2xl w-full text-left font-semibold">Report</span>
                      <span className="text-md"> Preview and download survey report</span>
                    </span>
                  </span>
                </Link>
              </div>
            </div>
          )}
        </>
      </div>
    </React.Fragment>
  );
};

export default Dashboard;
